import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { CircleX, EllipsisVertical, History, Pencil } from 'lucide-react';
import FormRenderer from './FormRenderer';

export default function AssessmentCard({
  assessment,
  template,
  version,
  latestRevision,
  revisionHistory,
  userId,
  canAdminister = false,
  onCorrect,
  onEnteredInError,
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorReason, setErrorReason] = useState('');

  // Author or Clinic Admin can mark entered in error
  const canMarkError = assessment.author_id === userId || canAdminister;
  // Any clinician with access to assessment can correct it
  const canCorrect = true;
  const hasRevisionHistory = revisionHistory.length > 1;
  const canAddCorrection = assessment.status === 'submitted' && canCorrect;
  const canMarkErrorAction = assessment.status === 'submitted' && canMarkError;
  const hasAssessmentActions = canAddCorrection || hasRevisionHistory || canMarkErrorAction;

  const handleConfirmError = e => {
    e.preventDefault();
    if (!errorReason.trim()) return;
    onEnteredInError(assessment, errorReason.trim());
    setErrorDialogOpen(false);
    setErrorReason('');
  };

  return (
    <article
      className={`rounded-xl border p-4 space-y-3 ${
        assessment.status === 'entered_in_error'
          ? 'border-red-200 bg-red-50/50'
          : 'border-slate-200 bg-white'
      }`}
      data-testid={`assessment-${assessment.id}`}
    >
      <header className="flex items-start justify-between gap-2 pb-2 border-b">
        <div className="min-w-0">
          <h4 className="font-semibold text-slate-900">
            {template?.name || 'Unavailable assessment template'}
          </h4>
          <p className="text-xs text-slate-500">
            Template v{version?.version_number ?? '—'} · Revision {latestRevision?.revision_number ?? '1'}
          </p>
          <p className="text-xs text-slate-500">
            Author: {assessment.doctorName || 'Unknown Doctor'}
            {latestRevision?.authorName && latestRevision.authorName !== assessment.doctorName && (
              ` (Rev ${latestRevision.revision_number} by ${latestRevision.authorName})`
            )}
            {latestRevision?.submitted_at ? ` · ${new Date(latestRevision.submitted_at).toLocaleString()}` : (assessment.created_at && ` · ${new Date(assessment.created_at).toLocaleString()}`)}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {hasAssessmentActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 shrink-0 rounded-full text-slate-600 hover:bg-slate-100"
                  aria-label="Assessment actions"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canAddCorrection && (
                  <DropdownMenuItem onSelect={() => onCorrect(assessment, latestRevision)}>
                    <Pencil aria-hidden="true" />
                    Add correction
                  </DropdownMenuItem>
                )}
                {hasRevisionHistory && (
                  <DropdownMenuItem onSelect={() => setHistoryOpen(value => !value)}>
                    <History aria-hidden="true" />
                    {historyOpen ? 'Hide revision history' : 'View revision history'}
                  </DropdownMenuItem>
                )}
                {canMarkErrorAction && (
                  <>
                    {(canAddCorrection || hasRevisionHistory) && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => setErrorDialogOpen(true)}
                    >
                      <CircleX aria-hidden="true" />
                      Mark entered in error
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {assessment.sync_status === 'pending' && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
              Pending synchronization
            </span>
          )}
          {assessment.sync_status === 'failed' && (
            <span role="alert" className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-200">
              Synchronization failed
            </span>
          )}
          {assessment.status === 'entered_in_error' && (
            <span className="text-xs text-red-700 font-medium bg-red-100 px-2 py-0.5 rounded border border-red-200">
              Entered in error
            </span>
          )}
        </div>
      </header>

      {assessment.status === 'entered_in_error' && (
        <div className="text-sm text-red-900 bg-red-100/80 p-3 rounded-lg border border-red-200 space-y-1">
          <p className="font-semibold text-xs uppercase tracking-wider text-red-800">Entered in Error Record</p>
          <p className="text-xs text-red-700 font-medium">
            Marked in error by {assessment.entered_in_error_actor_name || assessment.doctorName || 'Unknown Clinician'}
            {assessment.entered_in_error_at && ` at ${new Date(assessment.entered_in_error_at).toLocaleString()}`}
          </p>
          <p className="italic">&quot;{assessment.entered_in_error_reason}&quot;</p>
        </div>
      )}

      {version && latestRevision && (
        <FormRenderer
          schema={version.schema}
          answers={latestRevision.answers}
          readOnly
          errors={[]}
        />
      )}

      {historyOpen && (
        <ol className="mt-3 space-y-3 pl-4 border-l-2 border-slate-300 bg-slate-50 p-3 rounded-lg">
          {revisionHistory.map(revision => (
            <li key={revision.id} className="space-y-2">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-bold text-slate-800">
                  Revision {revision.revision_number} {revision.authorName ? `by ${revision.authorName}` : ''}
                </h5>
                <span className="text-xs text-slate-500">
                  {revision.submitted_at ? new Date(revision.submitted_at).toLocaleString() : ''}
                </span>
              </div>
              {revision.correction_reason && (
                <p className="text-xs text-slate-700 bg-amber-50 border border-amber-200 p-2 rounded italic">
                  Correction reason: {revision.correction_reason}
                </p>
              )}
              {version && (
                <FormRenderer schema={version.schema} answers={revision.answers} readOnly errors={[]} />
              )}
            </li>
          ))}
        </ol>
      )}

      {/* Required reason confirmation dialog for Entered in Error */}
      <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark assessment as entered in error</DialogTitle>
            <DialogDescription>
              Entered-in-error records remain permanently preserved in clinical history but are separated from valid patient assessments.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfirmError} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor={`error-reason-${assessment.id}`}>Reason for marking in error *</Label>
              <Input
                id={`error-reason-${assessment.id}`}
                value={errorReason}
                onChange={e => setErrorReason(e.target.value)}
                placeholder="e.g. Accidental entry for wrong patient"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setErrorDialogOpen(false)}>
                Cancel
              </Button>

              <Button type="submit" variant="destructive" disabled={!errorReason.trim()}>
                Mark entered in error
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </article>
  );
}

AssessmentCard.propTypes = {
  assessment: PropTypes.object.isRequired,
  template: PropTypes.object,
  version: PropTypes.object,
  latestRevision: PropTypes.object,
  revisionHistory: PropTypes.array.isRequired,
  userId: PropTypes.string.isRequired,
  canAdminister: PropTypes.bool,
  onCorrect: PropTypes.func.isRequired,
  onEnteredInError: PropTypes.func.isRequired,
};
